import React, { memo, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { store } from "../../../store/configureStore";
import moment from "moment";

// components
import { FormSidebar } from "../../_commons/FormSidebar";
import { Topbar } from "../../_commons/Topbar";
import SkeletonLoader from "../SkeletonLoader";
import ShareUrlIcon from "../../_icons/ShareUrlIcon";
import GraphPeriscope from "../ActivityLog/GraphPeriscope";
import MultiActionsButton from "../../_commons/MultiActionsButton";
import { SelectFilter } from "../../_commons/SelectFilter";
import { CustomTable } from "../../_commons/CustomTable";
import Popover from "../../_commons/Popover";
import { SelectFilterCustom } from "../../_commons/SelectFilterCustom";

// utils
import { ActionTypes } from "../../../actions/_types";
import { capitaliseText, lS, printCurrency, trackEvent } from "../../../atlas-utils";
import {
	calculateContinuousPercentages,
	formatDurationFromMinutes,
	minutesToTime,
	timeToMinutes,
	transformDataa
} from "../../../helpers/periscope";
import {
	CATALOGUE_PLATFORMS_LOGO,
	periscopeTimeStatus,
	periscopeTimeStatusSelectOptions
} from "../../../client-config";

// fetch
import {
	fetchActivityLogLocationAvailabilityTimeSeries,
	fetchLocationReasonCodeCount,
	fetchStoreDownTime,
	periscopeFetchStoreStatus,
	storeStatusToggle
} from "../../../actions/periscope";

const StoreDetailView = ({
	isFormOpen,
	configItems,
	periscopeCountryWiseSupportedPlatforms,
	periscopeLocationAvailabilityData,
	login,
	currencySymbol
}) => {
	const handleCancel = () => {
		store.dispatch({
			type: ActionTypes.LOCATION_AVAILABILITY_PERISCOPE_GRAPH_DATA,
			payload: {
				loading: false,
				error: false,
				data: {},
				sideBarOpen: false,
				storeAction: {},
				storeActionVariables: {}
			}
		});
	};
	const bizId = String(login?.loggedInbizDetail?.id);
	const country = (login?.loggedInbizDetail?.country || lS.get("auth")?.biz?.country).toLowerCase();
	const isMultibrandEnabled = login?.loggedInbizDetail?.isMultibrandEnabled;
	const bizPlatforms = useMemo(() => {
		return configItems?.bizPlatforms?.items.filter((platform) => {
			return (periscopeCountryWiseSupportedPlatforms?.[country] ||
				lS.get("periscopeCountryWisePlatforms")?.[country])?.[platform.platformName.toLowerCase()]?.enabled;
		});
	}, [configItems?.bizPlatforms?.items, periscopeCountryWiseSupportedPlatforms?.[country]]);

	const platform = periscopeLocationAvailabilityData?.graph?.platform;
	const currPlatform =
		platform &&
		(configItems?.bizPlatforms?.items || []).length > 0 &&
		configItems?.bizPlatforms?.items.find(
			(plt) => (plt.platformName || "").toLowerCase() === (platform || "").toLowerCase()
		);
	const currRowValue = periscopeLocationAvailabilityData?.graph?.currRowValue;
	const loading = periscopeLocationAvailabilityData?.graph?.loading;
	const error = periscopeLocationAvailabilityData?.graph?.error;
	const storeAction = periscopeLocationAvailabilityData?.graph?.storeAction;
	const storeActionVariables = periscopeLocationAvailabilityData?.graph?.storeActionVariables;
	const storeActionLoading = periscopeLocationAvailabilityData?.graph?.storeActionLoading;
	const storeActionError = periscopeLocationAvailabilityData?.graph?.storeActionError;

	const actionState = (storeAction?.locationPlatform?.state || "").toLowerCase();
	const storeStatus =
		actionState == "enabled" || actionState == "disabled" ? (actionState == "enabled" ? "Online" : "Offline") : "";

	const statusTextColor = storeStatus == "Offline" ? "c-b02b2b" : storeStatus == "Online" ? "c-0da125" : "";

	const data = useMemo(() => {
		return periscopeLocationAvailabilityData?.graph?.data?.data || [];
	}, [periscopeLocationAvailabilityData]);
	const [downTimeMetrics, setDownTimeMetrics] = useState({
		downtimeMins: "",
		avgMonetaryLoss: "",
		loading: false,
		error: false
	});
	const getStoredownTimeVariables = (timeValue = "", plt = "") => {
		if (!storeActionVariables?.filters) {
			return {};
		}
		return {
			bizId: String(bizId),
			platform: plt ? plt : periscopeLocationAvailabilityData?.graph?.variables?.platform,
			locationId: currRowValue?.location?.id,
			time: timeValue ? timeValue : periscopeLocationAvailabilityData?.graph?.variables?.time,
			...(isMultibrandEnabled && currRowValue?.brand?.id ? { brandId: currRowValue?.brand?.id } : {})
		};
	};

	const fetchMetricValues = async (timeValue = "", plt = "") => {
		try {
			setDownTimeMetrics({
				loading: true,
				error: false
			});
			const resp = await fetchStoreDownTime(
				getStoredownTimeVariables(timeValue ? timeValue : "", plt ? plt : "")
			);
			if (resp) {
				setDownTimeMetrics({
					downtimeMins: formatDurationFromMinutes(resp?.data?.getDowntimeInfo?.downtimeMins),
					avgMonetaryLoss: resp?.data?.getDowntimeInfo?.avgMonetaryLoss ?? 0,
					loading: false,
					error: false
				});
			}
		} catch (err) {
			setDownTimeMetrics({
				downtimeMins: "",
				avgMonetaryLoss: "",
				loading: false,
				error: true
			});
		}
	};

	const fetchStoreAction = async (variables = storeActionVariables) => {
		store.dispatch({
			type: ActionTypes.LOCATION_AVAILABILITY_PERISCOPE_GRAPH_DATA,
			payload: {
				storeActionLoading: true
			}
		});
		try {
			const resp = await periscopeFetchStoreStatus(variables);
			store.dispatch({
				type: ActionTypes.LOCATION_AVAILABILITY_PERISCOPE_GRAPH_DATA,
				payload: {
					storeAction: resp,
					storeActionLoading: false
				}
			});
		} catch (err) {
			store.dispatch({
				type: ActionTypes.LOCATION_AVAILABILITY_PERISCOPE_GRAPH_DATA,
				payload: {
					storeActionLoading: false,
					storeActionError: true
				}
			});
		}
	};

	useEffect(() => {
		if (isFormOpen) {
			if (Object.keys(storeAction).length == 0) {
				fetchStoreAction();
			}
			fetchMetricValues();
		}
	}, [isFormOpen]);

	const title =
		currRowValue &&
		`${currRowValue?.location?.name ? `${capitaliseText(currRowValue.location.name)} ` : ""}${
			isMultibrandEnabled && currRowValue?.brand?.name ? `| ${capitaliseText(currRowValue.brand.name)}` : ""
		}`;

	const tabs = useMemo(() => {
		return (
			currRowValue &&
			bizPlatforms
				.map((plt) => {
					const platformKey = (plt?.platformName || "").toLowerCase();

					if (!(platformKey in currRowValue)) {
						return null;
					}

					const value = String(currRowValue[platformKey]?.is_available);

					const cond = value === "true" || value === "false";

					if (!cond) return null;

					return {
						label: capitaliseText(plt?.platformName || ""),
						value: (plt?.platformName || "").toLowerCase(),
						id: plt?.id,
						title: capitaliseText(plt?.platformName || "")
					};
				})
				.filter(Boolean)
		);
	}, [bizPlatforms, currRowValue]);

	const getStoreToggleVariables = () => {
		const locationId = currRowValue && Number(currRowValue?.location?.id);
		const brandId = currRowValue && currRowValue?.brand?.id && Number(currRowValue?.brand?.id);

		return {
			locationIds: [locationId],
			action: "enable",
			...(brandId && isMultibrandEnabled ? { brand: brandId } : {}),
			snoozeDatetime: ""
		};
	};

	const [storeToggleLoading, setStoreToggleLoading] = useState(false);
	const handleUpdateStoreStatus = async (storeToggleVariables, platform = "") => {
		if (storeToggleLoading) {
			return;
		}
		setStoreToggleLoading(true);
		trackEvent("storefront_set_online_triggered", {
			platform_name: platform ? platform : "All",
			origin: "periscope_drawer_manual"
		});
		const resp = await storeStatusToggle(storeToggleVariables, platform);
		setStoreToggleLoading(false);
	};

	const handleCreateNew = () => {
		if (platform && (bizPlatforms || []).length) {
			let pltId = bizPlatforms.find(
				(plt) => (plt.platformName || "").toLowerCase() == (platform || "").toLowerCase()
			)?.id;
			pltId = pltId ? Number(pltId) : null;
			if (!pltId) return;
			handleUpdateStoreStatus(
				{
					...getStoreToggleVariables(),
					platformIds: [pltId]
				},
				capitaliseText(platform)
			);
		}
	};

	let multiFormAction = platform &&
		(bizPlatforms || []).length && [
			{
				title: `On ${capitaliseText(platform)}`,
				clickHandler: () => {
					let pltId = bizPlatforms.find(
						(plt) => (plt.platformName || "").toLowerCase() == (platform || "").toLowerCase()
					)?.id;
					pltId = pltId ? Number(pltId) : null;
					if (!pltId) return;
					handleUpdateStoreStatus(
						{
							...getStoreToggleVariables(),
							platformIds: [pltId]
						},
						capitaliseText(platform)
					);
				}
			}
		];

	if (multiFormAction) {
		let allValidPlatforms = bizPlatforms.reduce(
			(acc, curr) => {
				const platformKey = (curr?.platformName || "").toLowerCase();
				if (!(platformKey in currRowValue)) {
					return acc;
				}
				const value = String(currRowValue[platformKey]?.is_available);
				const cond = value === "true" || value === "false";
				if (cond) {
					acc["pltIds"].push(Number(curr.id));
					acc["names"] += "," + platformKey;
				}
				return acc;
			},
			{ pltIds: [], names: "", title: "On All Platforms" }
		);
		if (allValidPlatforms.pltIds.length > 1) {
			allValidPlatforms["clickHandler"] = () => {
				handleUpdateStoreStatus({
					...getStoreToggleVariables(),
					platformIds: allValidPlatforms?.pltIds
				});
			};
			multiFormAction.push(allValidPlatforms);
		}
	}

	const switchTab = (tab, tabClick = false) => {
		const variables = periscopeLocationAvailabilityData?.graph?.variables;
		store.dispatch({
			type: ActionTypes.LOCATION_AVAILABILITY_PERISCOPE_GRAPH_DATA,
			payload: {
				platform: tab.value,
				variables: { ...variables, platform: tab.value }
			}
		});
		fetchStoreAction({
			...storeActionVariables,
			filters: [
				{ field: "platform", value: tab.value },
				{ field: "action", value: "disable,enable" },
				{ field: "status", value: "success" },
				{ field: "created", value: "LAST_90_DAYS" }
			]
		});
		fetchMetricValues("", tab.value);
		fetchActivityLogLocationAvailabilityTimeSeries({ ...variables, platform: tab.value }, "location");
	};

	const [timeOption, setTimeOption] = useState("last_7_days");
	useEffect(() => {
		setTimeOption("last_7_days");
	}, [isFormOpen]);

	const handleTimeFilter = (val) => {
		const variables = periscopeLocationAvailabilityData?.graph?.variables;
		if (val?.value) {
			setTimeOption(val.value);
			const timeShortLabel = periscopeTimeStatusSelectOptions.find(
				(timeFilter) => timeFilter.value == val.value
			).shortLabel;
			if (timeShortLabel) {
				trackEvent("storefront_availability_time_filter_changed", {
					platform_name: capitaliseText(platform),
					selected_time_range: timeShortLabel
				});
			}
			store.dispatch({
				type: ActionTypes.LOCATION_AVAILABILITY_PERISCOPE_GRAPH_DATA,
				payload: {
					variables: { ...variables, time: val.value }
				}
			});
			fetchMetricValues(val.value);
			fetchActivityLogLocationAvailabilityTimeSeries({ ...variables, time: val.value }, "location");
		}
	};

	const values = (data || []).map((chunk, index) => {
		const chunkData = [...chunk.data];
		const percentages = calculateContinuousPercentages(chunkData, index, timeOption, data.length) || [];
		return percentages.reverse() || [];
	});
	let intervalScoring =
		(values || []).length > 0 &&
		(values || []).length < 2 &&
		(timeOption === "last_6_hours" ||
			timeOption === "last_2_hours" ||
			timeOption === "last_1_hour" ||
			timeOption === "last_12_hours")
			? values[0]?.[0]?.["end_time"]
			: false;

	const splitInterval = {
		last_6_hours: {
			interval: 12,
			value: 30
		},
		last_2_hours: {
			interval: 4,
			value: 30
		},
		last_1_hour: {
			interval: 4,
			value: 15
		},
		last_12_hours: {
			interval: 12,
			value: 60
		}
	};

	let timeIntervals = [];
	if (
		(data || []).length == 1 &&
		(timeOption === "last_12_hours" ||
			timeOption === "last_6_hours" ||
			timeOption === "last_2_hours" ||
			timeOption === "last_1_hour")
	) {
		let endTimeRange = timeToMinutes(intervalScoring);
		timeIntervals.push({ endTimeRange, time: intervalScoring });
		for (let i = splitInterval[timeOption]?.["interval"]; i > 0; i--) {
			endTimeRange -= splitInterval[timeOption]?.["value"];
			timeIntervals.push({ endTimeRange, time: minutesToTime(endTimeRange) });
		}
	}

	const [result, keys] = transformDataa(values);
	const processedData = result;

	const otherParamters = {};

	if (processedData.length > 15) {
		otherParamters["width"] = data.length * 48;
	}
	if (processedData.length == 15) {
		otherParamters["width"] = data.length * 42;
	}
	const numbers =
		(data || []).length == 1 &&
		(timeOption == "last_1_hour" || timeOption == "last_2_hours" || timeOption == "last_12_hours")
			? Array.from({ length: 5 }, () => data.length)
			: Array.from({ length: 25 }, () => data.length);
	function getDayOfWeeks(dateString) {
		const date = new Date(dateString);
		const options = { weekday: "long" };
		return date.toLocaleDateString("en-US", options);
	}

	const formatTime = (hour) => {
		let period = hour >= 12 ? "PM" : "AM";
		let displayHour = hour % 12;
		displayHour = displayHour === 0 ? 12 : displayHour;
		return `${displayHour}:00 ${period}`;
	};

	let reason = "";
	if (storeAction?.reasonCode) {
		reason = storeAction.reasonCode.split("_").map(capitaliseText).join(" ");
	}
	let updatedStoreTime = "";
	if (storeAction?.updated) {
		const date = moment(storeAction?.updated);
		const now = moment();

		if (date.year() === now.year()) {
			updatedStoreTime = date.format("DD MMM, hh:mm A");
		} else {
			updatedStoreTime = date.format("DD MMM YYYY, hh:mm A");
		}
	}

	let user =
		(storeAction && storeAction?.user?.firstName) ||
		storeAction?.user?.lastName ||
		storeAction?.user?.fullName ||
		storeAction?.user?.email;

	const handleSelectPlt = (value) => {
		value && value?.clickHandler && value.clickHandler();
	};
	const renderPopover = (data) => {
		return <div className="renderpopover"> {data}</div>;
	};

	return (
		<div className="sidebar-graph-periscope">
			<FormSidebar
				isOpen={isFormOpen}
				close={handleCancel}
				submit={() => {}}
				loading={false}
				disabled={true}
				hideSubmitAction={true}
				cancelTitle={"Close"}
				cancelClass={"close-btn"}
			>
				<Header title={title} handleCancel={handleCancel} />
				{tabs && tabs.length > 0 && (
					<Topbar tabs={tabs} selectedTab={platform} switchTab={(tab) => switchTab(tab, true)} />
				)}
				<div className="side-bar-location-content">
					<div className="store-action">
						<div className="action-details">
							{currPlatform && (
								<img
									className="plt-img"
									src={
										currPlatform?.image ||
										CATALOGUE_PLATFORMS_LOGO[currPlatform.platformName.toLowerCase()]
									}
								/>
							)}

							<div className="action-info">
								{storeActionLoading || storeActionError ? (
									<div className="action-loading">
										<SkeletonLoader classname={"large"} />
										<SkeletonLoader classname={"small"} />
									</div>
								) : (
									<>
										{updatedStoreTime && (
											<div className="final-text">
												<span className="normal-txt">Set</span>
												<span className={`store-status ${statusTextColor} `}>
													{storeStatus}
												</span>
												{user && <span className="normal-txt">by</span>}
												<span className="bold-text action-user">
													{(user || "").length > 15 ? (
														<Popover
															data={user}
															showOnHover={true}
															showOnClick
															renderPopover={renderPopover}
														>
															{user.slice(0, 15)}...
														</Popover>
													) : (
														<span>{user ? user : ""}</span>
													)}
												</span>
												{updatedStoreTime && <span className="normal-txt">at</span>}
												<span className="bold-text">{updatedStoreTime}</span>
											</div>
										)}

										{!storeActionLoading && (
											<div className="reason-txt">
												{storeAction?.reasonCode ? (
													<span className="reason-popover">
														{(reason || "").length > 50 ? (
															<Popover
																data={reason}
																showOnHover={true}
																showOnClick
																renderPopover={renderPopover}
															>
																{reason.slice(0, 50)}...
															</Popover>
														) : (
															<span>{reason ? reason : ""}</span>
														)}
													</span>
												) : !(
														storeAction &&
														Object.prototype.hasOwnProperty.call(storeAction, "updated")
												  ) ? (
													<div>No actions in the last 90 days</div>
												) : (
													""
												)}
											</div>
										)}
									</>
								)}
							</div>
						</div>
						<div
							className={`right-side-actions ${storeToggleLoading ? "store-toggle-loading" : ""} ${(multiFormAction || []).length == 1 ? "single-plt" : "multi-plt"} `}
						>
							{platform && (
								<div
									className="plt-link"
									onClick={() => {
										trackEvent("periscope_external_url_clicked", {
											platform_name: capitaliseText(platform)
										});
										window.open(currRowValue?.[(platform || "").toLowerCase()]?.url, "_blank");
									}}
								>
									View on {capitaliseText(platform)}
									<ShareUrlIcon styles={{ marginLeft: "3px" }} color="#3B5FEB" />
								</div>
							)}

							{(multiFormAction || []).length == 1 ? (
								<MultiActionsButton
									title="Set Online"
									instantAction={() => handleCreateNew()}
									actions={multiFormAction}
									showChevron={(multiFormAction || []).length > 1}
								/>
							) : (
								<SelectFilterCustom
									classes="plt-dropdown"
									options={multiFormAction}
									setFilter={(f, value) => handleSelectPlt(value)}
									labelKey={"title"}
									valueKey={"title"}
									isSearchable={false}
									placeholder="Set Online"
									ellipsizedLength={18}
								/>
							)}
						</div>
					</div>

					<div className="action-stats">
						<div className="left-content">
							<div className="mini-head">Availability Stats</div>
							<div className="metrics">
								<MetricSummaryPanel
									title="Total Downtime"
									value={downTimeMetrics?.downtimeMins}
									error={downTimeMetrics?.error}
									loading={downTimeMetrics?.loading}
									description={
										"Indicates the duration the store was offline during its scheduled working hours, based on store timings configured in Atlas (if available)."
									}
								/>
								<MetricSummaryPanel
									title="Estimated Lost Revenue"
									value={downTimeMetrics?.avgMonetaryLoss}
									loading={downTimeMetrics?.loading}
									error={downTimeMetrics?.error}
									description={
										"Estimated value of sales lost during the time the store was offline, based on past revenue patterns."
									}
									currencySymbol={currencySymbol}
								/>
							</div>
						</div>
						<SelectFilter
							containerClasses="location-time-dropdown"
							options={periscopeTimeStatusSelectOptions}
							field="timeOption"
							currValue={{
								value: timeOption,
								valueForDisplay: (
									<span className="status-main">
										<span className="status-display">{periscopeTimeStatus[timeOption]}</span>
									</span>
								)
							}}
							labelKey="valueForDisplay"
							valueKey="value"
							setFilter={(f, v) => handleTimeFilter(v)}
							isSearchable={false}
							isClearable={false}
						/>
					</div>

					<div className={`graph-div   ${loading ? `loading-div` : ``}  ${error ? " error" : "flex"} `}>
						{loading || error ? (
							<>
								<div className="shimmer H(100px)  " />
								<div className="shimmer H(100px) Mt(5px) " />
								<div className="shimmer H(100px) Mt(5px) " />
							</>
						) : (
							(data || []).length !== 0 && (
								<>
									<div
										className={`time-series flex ${
											timeOption == "last_1_hour" && data.length == 1 ? "mt-70" : ""
										}`}
									>
										<div
											className={`time-text ${
												!intervalScoring ? "justify-center" : "justify-between"
											}`}
										>
											{!intervalScoring
												? numbers.map((num, index) => {
														return (
															<div className="time">
																{index == 0 ||
																index == 6 ||
																index == 12 ||
																index == 18 ||
																index == 24
																	? index == 24
																		? "11:59PM"
																		: formatTime(index)
																	: ""}
															</div>
														);
													})
												: timeIntervals.reverse().map((time) => {
														return (
															<div className={`${time?.time ? "time" : ""}`}>
																{time?.time}
															</div>
														);
													})}
										</div>
										<div
											className={`border-interval ${
												timeOption == "last_1_hour" ||
												timeOption == "last_2_hours" ||
												timeOption == "last_6_hours"
													? "justify-between"
													: ""
											}`}
										>
											{numbers.map((num, index) => {
												return (
													<div
														className={`indicator ${
															timeOption == "last_1_hour" && data.length == 1
																? "justify-between"
																: "h-13"
														} `}
													>
														<div className="div-with"></div>
													</div>
												);
											})}
										</div>
									</div>
									<div
										className={`graph-content   ${processedData.length > 15 ? "scroll-x-hidden" : ""}`}
									>
										<div
											className={`date-display 
																	${processedData.length <= 14 ? "len-four-teen" : ""}
																	${processedData.length === 15 ? "len-five-teen" : ""}
																	${processedData.length >= 30 ? "len-three-ty" : ""}
																  `}
										>
											{processedData.map((num, index) => {
												return (
													<div
														className={`date-val ${
															processedData.length > 15 ? "len-greater-five-teen" : ""
														}
																		${processedData.length > 15 ? "len-greater-five-teen" : "less-five-teen"}
																		${processedData.length == 15 ? "len-eq-five-teen" : ""}
																		${index == 0 && processedData.length !== 15 ? "index-zero" : ""}
																		${processedData.length == 31 ? "len-three-one" : ""}
																		${index == 0 && processedData.length == 31 ? "index-zero-three-one" : ""}`}
														style={{
															...(processedData.length > 15
																? {}
																: {
																		width: "100%",
																		flexBasis: `${100 / (processedData || []).length}%`
																	})
														}}
													>
														<div className="date-txt">
															<div className="week">
																{processedData.length > 7
																	? getDayOfWeeks(
																			processedData[index]?.["day"]
																		).slice(0, 3)
																	: getDayOfWeeks(processedData[index]?.["day"])}
															</div>
															<div className="date">
																{processedData[index]?.day?.split("-")[2]}
															</div>
														</div>
													</div>
												);
											})}
										</div>
										<GraphPeriscope
											otherParamters={otherParamters}
											modified={processedData}
											keys={keys}
										/>
									</div>
								</>
							)
						)}
					</div>
					{currRowValue && platform && (
						<OfflineReasonSummary
							bizId={bizId}
							locationId={currRowValue?.location?.id}
							platform={platform}
							brandId={isMultibrandEnabled && currRowValue?.brand?.id ? currRowValue?.brand?.id : null}
						/>
					)}
				</div>
			</FormSidebar>
		</div>
	);
};

const mapStateToProps = (state) => ({
	configItems: state?.configItems,
	periscopeCountryWiseSupportedPlatforms: state.periscopeCountryWiseSupportedPlatforms,
	periscopeLocationAvailabilityData: state.periscopeLocationAvailabilityData,
	login: state.login,
	currencySymbol: state.login.loggedInbizDetail.currencySymbol
});
export default connect(mapStateToProps)(StoreDetailView);

const Header = ({ handleCancel, title }) => {
	return (
		<div className="header-sidebar" style={{ padding: "24px" }}>
			<div className="mini-div" onClick={() => handleCancel()}>
				<span className="mini-title">Storefront Availability</span>
				<img className="link-img" src="/assets/periscope/right-arrow-action.svg" />
			</div>
			<div className="main-title" style={{ marginTop: "8px" }}>
				{title}
			</div>
		</div>
	);
};

const OfflineReasonSummary = memo(({ bizId, locationId, platform, brandId = null }) => {
	const columns = useMemo(() => {
		return [
			{
				name: "Reason",
				field: "reason",
				render: (record, i) => {
					const reason = (record?.key || "").split("_").map(capitaliseText).join(" ");
					const renderPopover = (data) => {
						return <div className="renderpopover"> {data}</div>;
					};
					return (
						<div className="reason">
							{(reason || "").length > 50 ? (
								<Popover data={reason} showOnHover={true} showOnClick renderPopover={renderPopover}>
									{reason.slice(0, 50)}...
								</Popover>
							) : (
								<span>{reason ? reason : "--"}</span>
							)}
						</div>
					);
				}
			},
			{
				name: "Number of Occurrences",
				field: "occurances",
				render: (record, i) => <div className="occurances">{record.value}</div>
			}
		];
	}, []);

	const [data, setData] = useState();
	const [loading, setLoading] = useState(true);
	const fetchData = async () => {
		try {
			setLoading(true);
			const resp = await fetchLocationReasonCodeCount({
				bizId,
				locationId,
				locationAction: "disable",
				platform,
				...(brandId ? { brandId } : {})
			});

			if (resp.data.getLocationActionReasonCodeCount) {
				setData(resp.data.getLocationActionReasonCodeCount);
			} else {
				setData();
			}
		} catch (err) {
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (locationId && platform) {
			fetchData();
		} else {
			setData();
		}
	}, [locationId, platform]);

	return (
		<div className="offline-reason-content">
			<div style={{ marginBottom: "24px" }}>
				<span className="title">Offline Reason Summary</span> <span className="mini-title">(Last 30 days)</span>
			</div>

			{
				<CustomTable
					content="data"
					isRowClickable={false}
					classes="table-offline-reason"
					data={data}
					columns={columns}
					loading={loading}
				/>
			}
		</div>
	);
});

const MetricSummaryPanel = ({ title = "", value, loading, error, currencySymbol, description }) => {
	const renderPopover = (description) => {
		return (
			<div className="  pop-info">
				<span className="info-desc">{description}</span>
			</div>
		);
	};

	return (
		<div className="metric-summary-panel">
			<div className="metric-summary-panel-content">
				<div className="title">{title}</div>

				<Popover
					data={description}
					renderPopover={renderPopover}
					position={"down-left"}
					showOnHover={true}
					showOnClick
				>
					<img src="../../../assets/periscope/tooltip-icon.svg" className="info-icon" />
				</Popover>
			</div>

			{loading || error ? (
				<>
					<SkeletonLoader classname={`loading-metric ${error ? "error-metric" : ""} `} />
				</>
			) : (
				<div className="value">
					{currencySymbol ? printCurrency(currencySymbol) : ""}
					{value}
				</div>
			)}
		</div>
	);
};
